import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { PremoService } from '../../global-services/premo.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { CalculatePremoResponseModel } from '../../global-data-models/premo.model';
import { PatientService } from '../../global-services/patient.service';
import { Misc } from '../../global-misc/misc';

export interface CalculatePremoUiState {
  dashboardUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const EMPTY_CALCULATE_PREMO_UI_STATE: CalculatePremoUiState = {
  dashboardUrl: null,
  isLoading: false,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class CalculatePremoFacade {
  private uiState: CalculatePremoUiState = EMPTY_CALCULATE_PREMO_UI_STATE;
  private readonly _uiState$ = new BehaviorSubject<CalculatePremoUiState>(this.uiState);
  public readonly uiState$ = this._uiState$.asObservable();
  private readonly destroy$ = new Subject<void>();

  public constructor(
    private readonly premoService: PremoService,
    private readonly patientService: PatientService,
    private readonly loadingService: GlobalLoadingService,
    private readonly messageService: GlobalMessageService,
  ) {}

  public async initializeCalculatePremo(orgId: string, patientId: string): Promise<void> {
    try {
      this.updateUiState({ isLoading: true, error: null });
      this.loadingService.show();

      const response: CalculatePremoResponseModel = await this.premoService.calculatePremo(orgId, patientId);

      //Add patient name to meessage
      const patient = await this.patientService.getPatient(orgId, patientId);
      const firstName = patient?.firstName || 'Anonymous';
      const lastName = patient?.lastName || '';
      const patientName = patient ? `${Misc.captilizeFirstLetter(firstName)} ${Misc.captilizeFirstLetter(lastName)}` : 'Anonymous';
      const urlEncodedName = encodeURIComponent(patientName);

      if (response.success && response.dashboardUrl) {
        this.updateUiState({
          dashboardUrl: response.dashboardUrl+`&name=${urlEncodedName}`,
          isLoading: false,
          error: null,
        });
      } else {
        const errorMsg = response.errorMessage || 'Failed to calculate premo dashboard';
        this.updateUiState({
          error: errorMsg,
          isLoading: false,
        });
        this.messageService.showError('Calculate Premo Error', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An error occurred while calculating premo';
      this.updateUiState({
        error: errorMsg,
        isLoading: false,
      });
      this.messageService.showError('Calculate Premo Error', errorMsg);
    } finally {
      this.loadingService.hide();
    }
  }

  private updateUiState(updates: Partial<CalculatePremoUiState>): void {
    this.uiState = { ...this.uiState, ...updates };
    this._uiState$.next(this.uiState);
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
